import { useState, useEffect } from 'react';
import usePrintStore from '../store/printStore';
import { calculateBatchPricing, PricingGroup } from '../lib/supabase/api/calculate-batch-pricing';

export interface PreviewGroup {
  id: string;
  documentIds: string[];
  documentNames: string[];
  settingsSummary: string;
  totalPages: number;
  price: number;
  shopEarning: number; // What the shop receives for this group
  tierApplied: string;
  paperType: string;
  isColor: boolean;
}

interface UseGroupCalculationProps {
  documents: any[];
  printSettings: any;
  pricingLoaded: number;
  createSettingsSummary: (settings: any) => string;
}

export const useGroupCalculation = ({
  documents,
  printSettings,
  pricingLoaded,
  createSettingsSummary
}: UseGroupCalculationProps) => {
  const [previewGroups, setPreviewGroups] = useState<PreviewGroup[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    const calculatePreviewGroups = async () => {
      console.log('[useGroupCalculation] Starting calculation', { 
        documentsCount: documents.length,
        hasPrintSettings: !!printSettings 
      });

      if (documents.length === 0) {
        console.log('[useGroupCalculation] No documents, setting empty groups');
        setPreviewGroups([]);
        return;
      }

      const selectedShop = usePrintStore.getState().selectedShop;
      if (!selectedShop) {
        console.log('[useGroupCalculation] No shop selected');
        setPreviewGroups([]);
        return;
      }

      setIsCalculating(true);

      try {
        // Step 1: Group documents by print settings INCLUDING copies (for actual printing)
        const settingsGroups: { [key: string]: string[] } = {};
        
        documents.forEach(doc => {
          const settings = printSettings[doc.id];
          if (!settings) {
            console.log('[useGroupCalculation] No settings for document:', doc.id);
            return;
          }

          const settingsKey = `${settings.paperType || 'A4'}_${settings.color}_${settings.doubleSided}_${settings.orientation}_${settings.copies}`;
          
          if (!settingsGroups[settingsKey]) {
            settingsGroups[settingsKey] = [];
          }
          settingsGroups[settingsKey].push(doc.id);
        });

        console.log('[useGroupCalculation] Settings groups created:', Object.keys(settingsGroups).length);

        // Step 2: Create costing groups (IGNORE copies for pricing calculation)
        const costingGroups: { [key: string]: { totalPages: number; settingsKeys: string[] } } = {};
        
        Object.entries(settingsGroups).forEach(([settingsKey, docIds]) => {
          const firstDocId = docIds[0];
          const settings = printSettings[firstDocId];
          if (!settings) return;

          // Costing key WITHOUT copies
          const costingKey = `${settings.paperType || 'A4'}_${settings.color}_${settings.doubleSided}_${settings.orientation}`;
          
          if (!costingGroups[costingKey]) {
            costingGroups[costingKey] = { totalPages: 0, settingsKeys: [] };
          }
          
          // Calculate pages for this settings group
          let groupPages = 0;
          docIds.forEach(docId => {
            const docSettings = printSettings[docId];
            if (!docSettings) return;
            const pageCount = docSettings.effectivePageCount || 1;
            const copies = docSettings.copies || 1;
            groupPages += pageCount * copies;
          });
          
          costingGroups[costingKey].totalPages += groupPages;
          costingGroups[costingKey].settingsKeys.push(settingsKey);
        });

        console.log('[useGroupCalculation] Costing groups created:', Object.keys(costingGroups).length);

        // Step 3: Calculate pricing for each costing group
        const pricingGroups: PricingGroup[] = [];
        const costingKeyMap: { [key: string]: number } = {}; // Maps costing key to pricing group index
        
        Object.entries(costingGroups).forEach(([costingKey, data], index) => {
          const firstSettingsKey = data.settingsKeys[0];
          const firstDocId = settingsGroups[firstSettingsKey][0];
          const settings = printSettings[firstDocId];
          if (!settings) return;

          const paperType = settings.paperType || 'A4';
          const isColor = settings.color || false;

          pricingGroups.push({
            group_id: `costing_${index}`,
            paper_type: paperType,
            total_pages: data.totalPages,
            is_color: isColor
          });
          
          costingKeyMap[costingKey] = index;
        });

        console.log('[useGroupCalculation] Pricing groups prepared:', pricingGroups.length);

        // Step 4: Calculate tiered pricing for costing groups
        if (pricingGroups.length > 0) {
          console.log('[useGroupCalculation] Calling batch pricing API...');
          const pricingResult = await calculateBatchPricing(selectedShop, pricingGroups);
          console.log('[useGroupCalculation] Pricing result received:', pricingResult);

          // Step 5: Create preview groups (respecting copies) but using costing group pricing
          const preview: PreviewGroup[] = [];
          let displayIndex = 0;
          
          Object.entries(settingsGroups).forEach(([settingsKey, docIds]) => {
            const firstDocId = docIds[0];
            const settings = printSettings[firstDocId];
            if (!settings) return;
            
            // Get the costing key for this settings group
            const costingKey = `${settings.paperType || 'A4'}_${settings.color}_${settings.doubleSided}_${settings.orientation}`;
            const costingIndex = costingKeyMap[costingKey];
            const groupPricing = pricingResult.groups[costingIndex];

            let totalPages = 0;
            docIds.forEach(docId => {
              const docSettings = printSettings[docId];
              if (!docSettings) return;
              const pageCount = docSettings.effectivePageCount || 1;
              const copies = docSettings.copies || 1;
              totalPages += pageCount * copies;
            });

            const documentNames = docIds.map(docId => {
              const doc = documents.find(d => d.id === docId);
              return doc?.name || 'Unknown';
            });

            const settingsSummary = createSettingsSummary(settings);

            // Calculate price using the cost per page from the combined costing group
            const price = groupPricing.success 
              ? totalPages * groupPricing.cost_per_page 
              : 0;
            
            // Calculate shop earning using shop_earning_per_page from the tier
            const shopEarning = groupPricing.success
              ? totalPages * groupPricing.shop_earning_per_page
              : 0;

            preview.push({
              id: `preview_${displayIndex}`,
              documentIds: docIds,
              documentNames,
              settingsSummary,
              totalPages,
              price,
              shopEarning,
              tierApplied: groupPricing.success ? groupPricing.tier_applied : 'Error',
              paperType: settings.paperType || 'A4',
              isColor: settings.color || false
            });
            
            displayIndex++;
          });

          console.log('[useGroupCalculation] Preview groups created:', preview.length);
          setPreviewGroups(preview);
        } else {
          console.log('[useGroupCalculation] No pricing groups to calculate');
          setPreviewGroups([]);
        }
      } catch (error) {
        console.error('[useGroupCalculation] Error calculating preview groups:', error);
        // On error, still show groups but with fallback pricing
        try {
          const settingsGroups: { [key: string]: string[] } = {};
          
          documents.forEach(doc => {
            const settings = printSettings[doc.id];
            if (!settings) return;
            const settingsKey = `${settings.paperType || 'A4'}_${settings.color}_${settings.doubleSided}_${settings.orientation}_${settings.copies}`;
            if (!settingsGroups[settingsKey]) settingsGroups[settingsKey] = [];
            settingsGroups[settingsKey].push(doc.id);
          });

          const fallbackGroups = Object.entries(settingsGroups).map(([settingsKey, docIds], index) => {
            const firstDocId = docIds[0];
            const settings = printSettings[firstDocId];
            
            let totalPages = 0;
            docIds.forEach(docId => {
              const docSettings = printSettings[docId];
              if (!docSettings) return;
              const pageCount = docSettings.effectivePageCount || 1;
              const copies = docSettings.copies || 1;
              totalPages += pageCount * copies;
            });

            const documentNames = docIds.map(docId => {
              const doc = documents.find(d => d.id === docId);
              return doc?.name || 'Unknown';
            });

            // Fallback pricing calculation
            const pricePerPage = settings.color ? 12 : 3.5;
            const fallbackPrice = totalPages * pricePerPage;
            const fallbackShopEarning = totalPages * (settings.color ? 9.6 : 2.5); // Estimate shop portion

            return {
              id: `preview_${index}`,
              documentIds: docIds,
              documentNames,
              settingsSummary: createSettingsSummary(settings),
              totalPages,
              price: fallbackPrice,
              shopEarning: fallbackShopEarning,
              tierApplied: 'Estimated',
              paperType: settings.paperType || 'A4',
              isColor: settings.color || false
            };
          });

          console.log('[useGroupCalculation] Using fallback groups:', fallbackGroups.length);
          setPreviewGroups(fallbackGroups);
        } catch (fallbackError) {
          console.error('[useGroupCalculation] Fallback also failed:', fallbackError);
          setPreviewGroups([]);
        }
      } finally {
        setIsCalculating(false);
      }
    };

    calculatePreviewGroups();
  }, [documents, printSettings, pricingLoaded]);

  return { previewGroups, isCalculating };
};
